import { userInfoAtom, isLoggedInAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { ListingList } from 'app/components/listing-list';
import { Text } from 'app/design/primitives/text';
import type { NavigationScreen } from 'app/types/navigation';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useNavigationOptions } from 'app/hooks/navigation';
import { ListingType } from 'app/types/listing';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import Responsive from 'app/components/responsive';
import React from 'react';
import { getMyListingsOffersFilters, getMyListingsRequestsFilters } from 'app/network/listing-query-filters';

const NeighbourListingsScreenComponent: NavigationScreen<'neighbour-listings'> = ({ route, navigation }) => {
  const userInfo = useAtomValue(userInfoAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isMyself: boolean = typeof userInfo === 'object' && userInfo?.id === route.params.userId;
  const ns = isMyself ? 'my-listings' : 'neighbour-listings';
  const { t } = useTranslation(ns);

  const setPageTitle = usePageTitle();
  const { isSmallUI, isLargeUI } = useUISize();

  React.useEffect(
    () => setPageTitle(t(`title.${route.params.type === ListingType.request ? 'request' : 'offer'}`, { ns })),
    [route.params.type, setPageTitle, t, ns],
  );

  useNavigationOptions(
    navigation,
    {
      ...(navigation.getState().index === 0 ? { headerBackVisible: false } : {}), // Hide back button on Android when Close button is shown
      headerTitle: () => (
        <Text variant="bodyXLargeMedium">
          {t(`title.${route.params.type === ListingType.request ? 'request' : 'offer'}`, { ns })}
        </Text>
      ),
    },
    isSmallUI,
  );

  return (
    <ListingList
      ListHeaderComponent={
        <Responsive
          desktop={null}
          mobile={
            <Text fontWeight="600" marginBottom="spacing.general.x8" textAlign="center" variant="headerXSmall">
              {t(`title.${route.params.type}`)}
            </Text>
          }
        />
      }
      filters={{
        ...(isMyself
          ? route.params.type === ListingType.offer
            ? getMyListingsOffersFilters(isLoggedIn, route.params.userId)
            : getMyListingsRequestsFilters(isLoggedIn, route.params.userId)
          : {
              type: route.params.type,
              userId: route.params.userId,
              searchDistance: MAX_RADIUS_IN_METERS,
              includeCounts: true,
            }),
      }}
      listFormat={isLargeUI ? 'grid' : undefined}
      presentation={{
        searchBar: 'visible',
        requestCard: isLargeUI ? 'highest' : 'standard',
      }}
    />
  );
};

export const NeighbourListingsScreen: NavigationScreen<'neighbour-listings'> = withOptionalMetrics(
  'NeighbourListingsScreen',
  withErrorBoundary(NeighbourListingsScreenComponent),
);
